import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { PageLayout } from '../../../components/common/PageLayout';
import {
  DeficitResponse,
  EmployeeSearchCandidate,
  ManualProcessRow,
  PeriodOption,
  useAssignPayrollExternalId,
  useDeficitPeriods,
  useManagedDeficits,
  useManualProcessDeficits,
  useSearchEmployees,
  RowStatus,
} from '../../../hooks/useDeficitsApi';
import { deficitsNav } from '../nav';

type MismatchResolution = 'entered' | 'database';

interface ManualInputRow {
  id: string;
  adpId: string;
  drawCarryoverText: string;
}

async function copyTextToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    // Non-secure context or permission denied
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
}

function statusRowClass(status: RowStatus): string {
  switch (status) {
    case 'Success':
    case 'WouldUpdate':
      return 'bg-green-50 dark:bg-green-950/30';
    case 'Mismatch':
      return 'bg-yellow-50 dark:bg-yellow-950/30';
    case 'Skipped':
      return 'bg-surface-hover';
    default:
      return 'bg-red-50 dark:bg-red-950/30';
  }
}

function statusTextClass(status: RowStatus): string {
  switch (status) {
    case 'Success':
    case 'WouldUpdate':
      return 'text-green-700 dark:text-green-300';
    case 'Mismatch':
      return 'text-yellow-700 dark:text-yellow-300';
    case 'Skipped':
      return 'text-muted';
    default:
      return 'text-red-700 dark:text-red-300';
  }
}

function formatPeriodLabel(period: PeriodOption): string {
  return `${period.LowerDate} - ${period.UpperDate}`;
}

function formatMoney(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function candidateIsActive(c: EmployeeSearchCandidate): boolean {
  return c.IsActive;
}

const btnPrimary =
  'inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed';
const btnSecondary =
  'inline-flex items-center rounded-md border border-border-default bg-surface-alt px-4 py-2 text-sm font-medium text-primary hover:bg-surface-hover disabled:opacity-60 disabled:cursor-not-allowed';
const btnCompact =
  'inline-flex items-center rounded-md border border-border-default bg-surface-alt px-2 py-1 text-xs font-medium text-primary hover:bg-surface-hover disabled:opacity-60';

export const DeficitsPage: React.FC = () => {
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | null>(null);
  const [rows, setRows] = useState<ManualInputRow[]>([{ id: 'row-1', adpId: '', drawCarryoverText: '' }]);
  const [reviewReady, setReviewReady] = useState(false);
  const [mismatchResolutions, setMismatchResolutions] = useState<Record<number, MismatchResolution>>({});
  const [employeeLinkRow, setEmployeeLinkRow] = useState<number | null>(null);
  const [employeeLinkAdpId, setEmployeeLinkAdpId] = useState('');
  const [employeeLinkFirst, setEmployeeLinkFirst] = useState('');
  const [employeeLinkLast, setEmployeeLinkLast] = useState('');
  const [employeeLinkResults, setEmployeeLinkResults] = useState<EmployeeSearchCandidate[]>([]);
  const [employeeLinkPanelError, setEmployeeLinkPanelError] = useState<string | null>(null);
  const [adpLookupOpen, setAdpLookupOpen] = useState(false);
  const [adpLookupFirst, setAdpLookupFirst] = useState('');
  const [adpLookupLast, setAdpLookupLast] = useState('');
  const [adpLookupResults, setAdpLookupResults] = useState<EmployeeSearchCandidate[]>([]);
  const [adpLookupError, setAdpLookupError] = useState<string | null>(null);
  const [adpLookupCopyMessage, setAdpLookupCopyMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<DeficitResponse | null>(null);
  const [result, setResult] = useState<DeficitResponse | null>(null);

  const { data: periods = [], isLoading: periodsLoading } = useDeficitPeriods();
  const { data: manageReport, isLoading: manageLoading } = useManagedDeficits(selectedPeriodId, true);
  const manualProcess = useManualProcessDeficits();
  const searchEmployees = useSearchEmployees();
  const assignPayrollId = useAssignPayrollExternalId();

  useEffect(() => {
    document.title = 'Add Deficits — Corp Admin';
  }, []);

  useEffect(() => {
    if (periods.length > 0 && selectedPeriodId === null) {
      setSelectedPeriodId(periods[0].PeriodId);
    }
  }, [periods, selectedPeriodId]);

  const resetAddDeficits = useCallback(() => {
    setRows([{ id: `row-${Date.now()}`, adpId: '', drawCarryoverText: '' }]);
    setError(null);
    setPreview(null);
    setResult(null);
    setReviewReady(false);
    setMismatchResolutions({});
    setEmployeeLinkRow(null);
    setEmployeeLinkAdpId('');
    setEmployeeLinkFirst('');
    setEmployeeLinkLast('');
    setEmployeeLinkResults([]);
    setEmployeeLinkPanelError(null);
    setAdpLookupOpen(false);
    setAdpLookupFirst('');
    setAdpLookupLast('');
    setAdpLookupResults([]);
    setAdpLookupError(null);
    setAdpLookupCopyMessage(null);
  }, []);

  const hasAnyEnteredRows = useMemo(
    () => rows.some((r) => r.adpId.trim().length > 0 || r.drawCarryoverText.trim().length > 0),
    [rows]
  );

  const buildRequestRows = useCallback((): ManualProcessRow[] => {
    return rows
      .filter((r) => r.adpId.trim().length > 0 || r.drawCarryoverText.trim().length > 0)
      .map((r, idx) => ({
        Row: idx + 1,
        AdpId: r.adpId.trim(),
        DrawCarryoverText: r.drawCarryoverText.trim(),
        MismatchResolution: mismatchResolutions[idx + 1],
      }));
  }, [mismatchResolutions, rows]);

  const runManualProcess = useCallback(
    async (dryRun: boolean) => {
      if (!selectedPeriodId) return null;
      const requestRows = buildRequestRows();
      if (requestRows.length === 0) return null;

      setError(null);
      try {
        return await manualProcess.mutateAsync({
          periodId: selectedPeriodId,
          rows: requestRows,
          dryRun,
        });
      } catch (err) {
        const msg =
          (err as { response?: { data?: { ErrorMessage?: string } } })?.response?.data?.ErrorMessage ??
          (err as Error).message ??
          'Network error';
        setError(msg);
        return null;
      }
    },
    [buildRequestRows, manualProcess, selectedPeriodId]
  );

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      { id: `row-${Date.now()}-${prev.length + 1}`, adpId: '', drawCarryoverText: '' },
    ]);
    setReviewReady(false);
    setPreview(null);
    setResult(null);
    setMismatchResolutions({});
  }, []);

  const updateRow = useCallback((id: string, field: 'adpId' | 'drawCarryoverText', value: string) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    setReviewReady(false);
    setPreview(null);
    setResult(null);
    setMismatchResolutions({});
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((row) => row.id !== id)));
    setReviewReady(false);
    setPreview(null);
    setResult(null);
    setMismatchResolutions({});
  }, []);

  const reviewEntries = useCallback(async () => {
    const data = await runManualProcess(true);
    if (data) {
      setPreview(data);
      setResult(null);
      setReviewReady(true);
    }
  }, [runManualProcess]);

  const toggleEmployeeLinkPanel = useCallback((r: DeficitResponse['Rows'][number]) => {
    setEmployeeLinkPanelError(null);
    setEmployeeLinkResults([]);
    setEmployeeLinkRow((prev) => {
      if (prev === r.Row) return null;
      setEmployeeLinkAdpId(r.AdpId.trim());
      setEmployeeLinkFirst(r.FirstName ?? '');
      setEmployeeLinkLast(r.LastName ?? '');
      return r.Row;
    });
  }, []);

  const searchEmployeesForLink = useCallback(async () => {
    if (!selectedPeriodId) return;
    setEmployeeLinkPanelError(null);
    try {
      const data = await searchEmployees.mutateAsync({
        periodId: selectedPeriodId,
        firstName: employeeLinkFirst,
        lastName: employeeLinkLast,
      });
      setEmployeeLinkResults(data);
    } catch (err) {
      setEmployeeLinkPanelError((err as Error).message || 'Search failed.');
      setEmployeeLinkResults([]);
    }
  }, [employeeLinkFirst, employeeLinkLast, searchEmployees, selectedPeriodId]);

  const searchAdpLookup = useCallback(async () => {
    if (!selectedPeriodId) return;
    setAdpLookupError(null);
    try {
      const data = await searchEmployees.mutateAsync({
        periodId: selectedPeriodId,
        firstName: adpLookupFirst,
        lastName: adpLookupLast,
      });
      setAdpLookupResults(data);
    } catch (err) {
      setAdpLookupError((err as Error).message || 'Search failed.');
      setAdpLookupResults([]);
    }
  }, [adpLookupFirst, adpLookupLast, searchEmployees, selectedPeriodId]);

  const copyAdpFromLookup = useCallback(async (payrollExternalId: string) => {
    const trimmed = payrollExternalId.trim();
    if (!trimmed) return;
    try {
      await copyTextToClipboard(trimmed);
      setAdpLookupCopyMessage('ADP ID copied to clipboard.');
      window.setTimeout(() => setAdpLookupCopyMessage(null), 2500);
    } catch {
      setAdpLookupCopyMessage('Could not copy. Try selecting the ID manually.');
      window.setTimeout(() => setAdpLookupCopyMessage(null), 3500);
    }
  }, []);

  const assignPayrollExternalToEmployee = useCallback(
    async (employeeId: number) => {
      const adp = employeeLinkAdpId.trim();
      if (!adp) return;
      setEmployeeLinkPanelError(null);
      try {
        await assignPayrollId.mutateAsync({ employeeId, payrollExternalId: adp });
        setEmployeeLinkRow(null);
        await reviewEntries();
      } catch (err) {
        setEmployeeLinkPanelError((err as Error).message || 'Could not update employee.');
      }
    },
    [assignPayrollId, employeeLinkAdpId, reviewEntries]
  );

  const processDeficits = useCallback(async () => {
    if (!reviewReady) return;
    const data = await runManualProcess(false);
    if (data) setResult(data);
  }, [reviewReady, runManualProcess]);

  const displayData = result ?? preview;
  const showReviewData = reviewReady || result !== null;
  const unresolvedMismatchCount = useMemo(
    () => (preview?.Rows ?? []).filter((r) => r.Status === 'Mismatch' && !mismatchResolutions[r.Row]).length,
    [mismatchResolutions, preview]
  );
  const hasBlockingIssues = preview ? preview.ErrorCount > 0 || unresolvedMismatchCount > 0 : false;

  const processBatchTotal = useMemo(() => {
    if (!displayData?.Rows?.length) return 0;
    return displayData.Rows.reduce((sum, row) => {
      if (row.Status === 'Mismatch') {
        const resolution = mismatchResolutions[row.Row];
        if (resolution === 'entered' && row.DrawCarryover != null) return sum + row.DrawCarryover;
        if (resolution === 'database' && row.PreviousDrawCarryover != null)
          return sum + row.PreviousDrawCarryover;
        return sum;
      }
      if (row.Status === 'Success' || row.Status === 'WouldUpdate') {
        return sum + (row.DrawCarryover ?? 0);
      }
      return sum;
    }, 0);
  }, [displayData, mismatchResolutions]);

  const showReviewTable = showReviewData && displayData && displayData.Rows.length > 0;
  const showProcessDeficitsBlock = Boolean(showReviewTable);
  const showReviewCompleteHint =
    showProcessDeficitsBlock &&
    reviewReady &&
    Boolean(preview?.Rows?.length) &&
    !result &&
    !manualProcess.isPending &&
    !hasBlockingIssues;

  const renderEmployeeTable = (
    results: EmployeeSearchCandidate[],
    mode: 'lookup' | 'link'
  ) => (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border-default">
      <table className="w-full text-sm text-primary">
        <thead className="bg-surface-hover text-left text-xs uppercase text-muted">
          <tr>
            <th className="px-3 py-2">Employee ID</th>
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Active</th>
            <th className="px-3 py-2">{mode === 'lookup' ? 'ADP ID' : 'Current ADP'}</th>
            <th className="px-3 py-2">Timesheet this period</th>
            {mode === 'link' && <th className="px-3 py-2" />}
          </tr>
        </thead>
        <tbody>
          {results.map((c) => {
            const adp = c.PayrollExternalID?.trim() ?? '';
            const hasAdp = adp.length > 0;
            return (
              <tr
                key={c.EmployeeId}
                className={`border-t border-border-default ${!candidateIsActive(c) ? 'opacity-70' : ''}`}
              >
                <td className="px-3 py-2">{c.EmployeeId}</td>
                <td className="px-3 py-2">
                  {c.LastName}, {c.FirstName}
                </td>
                <td className="px-3 py-2">{candidateIsActive(c) ? 'Yes' : 'No (deleted)'}</td>
                <td className="px-3 py-2">
                  {mode === 'lookup' ? (
                    hasAdp ? (
                      <span className="inline-flex items-center gap-2">
                        <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-xs">{adp}</code>
                        <button type="button" className={btnCompact} onClick={() => void copyAdpFromLookup(adp)}>
                          Copy
                        </button>
                      </span>
                    ) : (
                      '—'
                    )
                  ) : (
                    c.PayrollExternalID ?? '—'
                  )}
                </td>
                <td className="px-3 py-2">{c.HasTimesheetThisPeriod ? 'Yes' : 'No'}</td>
                {mode === 'link' && (
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className={btnCompact + ' bg-blue-600 text-white hover:bg-blue-700'}
                      disabled={assignPayrollId.isPending}
                      onClick={() => void assignPayrollExternalToEmployee(c.EmployeeId)}
                    >
                      {assignPayrollId.isPending ? 'Saving…' : 'Use this record'}
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <PageLayout navItems={deficitsNav}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary">Add Deficits</h1>
        <p className="mt-1 text-sm text-muted">
          Enter ADP IDs and draw carryover values for a payroll period.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <button type="button" className={btnSecondary} onClick={resetAddDeficits}>
          Clear Form
        </button>
        <button
          type="button"
          className={btnSecondary}
          disabled={!selectedPeriodId || periodsLoading}
          onClick={() => {
            setAdpLookupOpen((open) => {
              const next = !open;
              if (next) {
                setAdpLookupError(null);
                setAdpLookupResults([]);
                setAdpLookupCopyMessage(null);
              }
              return next;
            });
          }}
        >
          {adpLookupOpen ? 'Close ADP ID lookup' : 'ADP ID Lookup'}
        </button>
      </div>

          {adpLookupOpen && (
            <div className="mb-6 rounded-lg border border-border-default bg-surface-hover p-4">
              <p className="mb-3 text-sm text-muted">
                Search by name for the selected payroll period. Use <strong>Copy</strong> next to an ADP ID to paste it
                into the form.
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-xs text-muted">
                  <span>First name contains</span>
                  <input
                    type="text"
                    className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-primary"
                    value={adpLookupFirst}
                    onChange={(e) => setAdpLookupFirst(e.target.value)}
                    autoComplete="off"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-muted">
                  <span>Last name contains</span>
                  <input
                    type="text"
                    className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-primary"
                    value={adpLookupLast}
                    onChange={(e) => setAdpLookupLast(e.target.value)}
                    autoComplete="off"
                  />
                </label>
                <button
                  type="button"
                  className={btnPrimary}
                  disabled={
                    searchEmployees.isPending ||
                    (!adpLookupFirst.trim() && !adpLookupLast.trim()) ||
                    !selectedPeriodId
                  }
                  onClick={() => void searchAdpLookup()}
                >
                  {searchEmployees.isPending ? 'Searching…' : 'Search'}
                </button>
              </div>
              {adpLookupError && <p className="mt-2 text-sm text-red-600">{adpLookupError}</p>}
              {adpLookupCopyMessage && (
                <p className="mt-2 text-sm font-medium text-blue-600" role="status">
                  {adpLookupCopyMessage}
                </p>
              )}
              {adpLookupResults.length > 0 && renderEmployeeTable(adpLookupResults, 'lookup')}
            </div>
          )}

          <section className="mb-6 rounded-lg border border-border-default bg-surface-alt p-4">
            <div className="flex flex-wrap items-end gap-8">
              <div>
                <p className="text-xs font-medium uppercase text-muted">Payroll Period</p>
                <select
                  className="mt-1 rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-primary"
                  value={selectedPeriodId ?? ''}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setSelectedPeriodId(Number.isNaN(value) ? null : value);
                    setReviewReady(false);
                    setPreview(null);
                    setResult(null);
                    setMismatchResolutions({});
                    setAdpLookupResults([]);
                    setAdpLookupError(null);
                  }}
                >
                  {periodsLoading && <option value="">Loading periods...</option>}
                  {!periodsLoading && periods.length === 0 && <option value="">No periods found</option>}
                  {periods.map((period) => (
                    <option key={period.PeriodId} value={period.PeriodId}>
                      {formatPeriodLabel(period)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-muted">Total Deficits</p>
                <p className="mt-1 text-sm font-medium text-primary">
                  {manageLoading || !manageReport ? '…' : formatMoney(manageReport.TotalDrawCarryover ?? 0)}
                </p>
              </div>
            </div>
          </section>

          <div className="overflow-x-auto rounded-lg border border-border-default">
            <table className="w-full text-sm text-primary">
              <thead className="bg-surface-hover text-left text-xs uppercase text-muted">
                <tr>
                  <th className="px-4 py-3">Row</th>
                  <th className="px-4 py-3">ADP ID</th>
                  <th className="px-4 py-3">Draw Carryover</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.id} className="border-t border-border-default">
                    <td className="px-4 py-3">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <input
                        className="w-full rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-primary"
                        value={row.adpId}
                        onChange={(e) => updateRow(row.id, 'adpId', e.target.value)}
                        placeholder="ADP ID"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        className="w-full rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-primary"
                        value={row.drawCarryoverText}
                        onChange={(e) => updateRow(row.id, 'drawCarryoverText', e.target.value)}
                        placeholder="0.00"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className={btnSecondary}
                        onClick={() => removeRow(row.id)}
                        disabled={rows.length === 1}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="button" className={btnPrimary} onClick={addRow}>
              Add Another Record
            </button>
            <button
              type="button"
              className={btnPrimary}
              onClick={() => void reviewEntries()}
              disabled={manualProcess.isPending || !hasAnyEnteredRows}
            >
              {manualProcess.isPending && manualProcess.variables?.dryRun ? 'Reviewing...' : 'Review Entries'}
            </button>
            {!reviewReady && !manualProcess.isPending && (
              <span className="text-sm font-medium text-red-600">Review entries before processing.</span>
            )}
          </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 dark:bg-red-950 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {showReviewData && displayData && (
        <div className="mt-6 rounded-lg border border-border-default bg-surface-alt p-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <p className="text-xs uppercase text-muted">Payroll End Date</p>
              <p className="text-primary">{displayData.PayrollEndDate}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted">Pay Date</p>
              <p className="text-primary">{displayData.PayDate}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-muted">Period ID</p>
              <p className="text-primary">{displayData.PeriodId}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="rounded-md bg-green-100 px-2 py-1 text-xs font-semibold text-green-700 dark:bg-green-950 dark:text-green-300">
              {displayData.DryRun ? 'Will Update' : 'Updated'}: {displayData.UpdatedCount}
            </span>
            <span className="rounded-md bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300">
              Mismatches: {displayData.MismatchCount}
            </span>
            <span className="rounded-md bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 dark:bg-red-950 dark:text-red-300">
              Errors: {displayData.ErrorCount}
            </span>
            <span className="rounded-md bg-surface-hover px-2 py-1 text-xs font-semibold text-muted">
              Skipped: {displayData.SkippedCount}
            </span>
          </div>
        </div>
      )}

      {showReviewTable && displayData && (
        <div className="mt-6 overflow-x-auto rounded-lg border border-border-default">
          <table className="w-full text-sm text-primary">
            <thead className="bg-surface-hover text-left text-xs uppercase text-muted">
              <tr>
                <th className="px-3 py-2">Last Name</th>
                <th className="px-3 py-2">First Name</th>
                <th className="px-3 py-2">ADP ID</th>
                <th className="px-3 py-2">Your Value</th>
                <th className="px-3 py-2">Database Value</th>
                <th className="px-3 py-2">Message</th>
              </tr>
            </thead>
            <tbody>
              {displayData.Rows.map((r) => {
                const yourAmount = r.DrawCarryover?.toFixed(2) ?? r.DrawCarryoverText;
                const dbAmount = r.PreviousDrawCarryover?.toFixed(2) ?? '—';
                const showMismatchPick = r.Status === 'Mismatch' && result === null;
                const showEmployeeLink =
                  r.Status === 'EmployeeNotFound' && result === null && Boolean(selectedPeriodId);

                return (
                  <Fragment key={r.Row}>
                    <tr className={`border-t border-border-default ${statusRowClass(r.Status)}`}>
                      <td className="px-3 py-2">{r.LastName}</td>
                      <td className="px-3 py-2">{r.FirstName}</td>
                      <td className="px-3 py-2">{r.AdpId}</td>
                      <td className="px-3 py-2">
                        {showMismatchPick ? (
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={mismatchResolutions[r.Row] === 'entered'}
                              onChange={() =>
                                setMismatchResolutions((prev) => ({ ...prev, [r.Row]: 'entered' }))
                              }
                            />
                            <span>{yourAmount}</span>
                          </label>
                        ) : (
                          yourAmount
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {showMismatchPick ? (
                          <label className="inline-flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={mismatchResolutions[r.Row] === 'database'}
                              onChange={() =>
                                setMismatchResolutions((prev) => ({ ...prev, [r.Row]: 'database' }))
                              }
                            />
                            <span>{dbAmount}</span>
                          </label>
                        ) : (
                          dbAmount
                        )}
                      </td>
                      <td className={`px-3 py-2 ${statusTextClass(r.Status)}`}>
                        <div>{r.Message}</div>
                        {showEmployeeLink && (
                          <button
                            type="button"
                            className={`mt-2 ${btnCompact}`}
                            onClick={() => toggleEmployeeLinkPanel(r)}
                          >
                            {employeeLinkRow === r.Row ? 'Close employee lookup' : 'Link ADP to employee…'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {employeeLinkRow === r.Row && showEmployeeLink && (
                      <tr>
                        <td colSpan={6} className="bg-surface-hover p-4">
                          <p className="mb-3 text-sm text-muted">
                            Link ADP ID <strong>{employeeLinkAdpId}</strong> to the correct{' '}
                            <strong>employee record</strong> (internal ID). Duplicate names are possible—prefer{' '}
                            <strong>Active</strong> rows, then rows with a timesheet this period.
                          </p>
                          <div className="flex flex-wrap items-end gap-3">
                            <label className="flex flex-col gap-1 text-xs text-muted">
                              <span>First name contains</span>
                              <input
                                type="text"
                                className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-primary"
                                value={employeeLinkFirst}
                                onChange={(e) => setEmployeeLinkFirst(e.target.value)}
                              />
                            </label>
                            <label className="flex flex-col gap-1 text-xs text-muted">
                              <span>Last name contains</span>
                              <input
                                type="text"
                                className="rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-primary"
                                value={employeeLinkLast}
                                onChange={(e) => setEmployeeLinkLast(e.target.value)}
                              />
                            </label>
                            <button
                              type="button"
                              className={btnPrimary}
                              disabled={
                                searchEmployees.isPending ||
                                (!employeeLinkFirst.trim() && !employeeLinkLast.trim())
                              }
                              onClick={() => void searchEmployeesForLink()}
                            >
                              {searchEmployees.isPending ? 'Searching…' : 'Search'}
                            </button>
                          </div>
                          {employeeLinkPanelError && (
                            <p className="mt-2 text-sm text-red-600">{employeeLinkPanelError}</p>
                          )}
                          {employeeLinkResults.length > 0 && renderEmployeeTable(employeeLinkResults, 'link')}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showProcessDeficitsBlock && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-6">
            <button
              type="button"
              className={btnPrimary}
              onClick={() => void processDeficits()}
              disabled={manualProcess.isPending || !reviewReady || hasBlockingIssues}
            >
              {manualProcess.isPending && !manualProcess.variables?.dryRun ? 'Processing...' : 'Process Deficits'}
            </button>
            <div>
              <p className="text-xs font-medium uppercase text-muted">Total Deficits</p>
              <p className="text-sm font-medium text-primary">{formatMoney(processBatchTotal)}</p>
            </div>
          </div>
          {hasBlockingIssues && (
            <p className="mt-2 text-sm font-medium text-red-600">
              Cannot process — resolve all errors and pick Your Value or Database Value for each mismatch.
            </p>
          )}
          {showReviewCompleteHint && (
            <p className="mt-2 text-sm text-muted">Review complete. Confirm to process.</p>
          )}
        </div>
      )}
    </PageLayout>
  );
};
